/**
 * Helpers puros da Projeção de cartões (matriz compra parcelada / recorrência × competência),
 * porte do ERPWebUI em centavos. A projeção vem completa do backend (todos os cartões); aqui vive
 * a única lógica do cliente: filtrar e somar conforme a visão (todos os cartões ou um só).
 */
import type { CreditCardProjection, ProjectionGroup } from '@/types/finance';

/** null = consolidado (todos os cartões). */
export type ProjectionView = number | null;

/** Linhas (parceladas e recorrências) da visão, na ordem do backend. */
export const projectionRowsForView = (data: CreditCardProjection, view: ProjectionView): ProjectionGroup[] =>
  data.groups.filter((g) => view === null || g.credit_card_id === view);

/** "Demais lançamentos" (avulsos e saldo anterior) da visão, somados por mês — alinhado a `months`. */
export const projectionOthersForView = (data: CreditCardProjection, view: ProjectionView): number[] => {
  const totals = data.months.map(() => 0);
  for (const other of data.others) {
    if (view !== null && other.credit_card_id !== view) continue;
    other.month_amounts.forEach((v, i) => {
      if (i < totals.length) totals[i] += v;
    });
  }
  return totals;
};

/**
 * Total do mês da visão (linhas + demais). No consolidado usa os `totals` do backend quando
 * vierem completos; num cartão único é a prévia do total da fatura.
 */
export const projectionMonthTotals = (data: CreditCardProjection, view: ProjectionView): number[] => {
  if (view === null && data.totals.length === data.months.length) return [...data.totals];
  const totals = projectionOthersForView(data, view);
  for (const g of projectionRowsForView(data, view)) {
    g.month_amounts.forEach((v, i) => {
      if (i < totals.length) totals[i] += v;
    });
  }
  return totals;
};

/** A visão tem algum valor (linha ou "demais")? */
export const projectionViewHasData = (data: CreditCardProjection, view: ProjectionView): boolean =>
  projectionRowsForView(data, view).length > 0 || projectionOthersForView(data, view).some((v) => v !== 0);

/** Soma de uma linha de valores. */
export const sumCents = (values: number[]): number => values.reduce((a, b) => a + b, 0);
